import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import type { NgsildYearRange } from '../ngsild.models';

@Component({
  selector: 'lib-ngsild-year-range-field',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './year-range-field.component.html',
  styleUrl: './year-range-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => YearRangeFieldComponent),
      multi: true,
    },
  ],
})
export class YearRangeFieldComponent implements ControlValueAccessor {
  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() placeholder = 'Choose year range';
  @Input() minYear = 1900;
  @Input() maxYear = new Date().getFullYear();
  @Input() hasError = false;

  isOpen = false;
  disabled = false;
  yearRange: NgsildYearRange = { startYear: null, endYear: null };
  currentDecade = Math.max(
    2000,
    Math.floor((new Date().getFullYear() - 20) / 20) * 20,
  );
  selectedStartYear: number | null = null;
  selectedEndYear: number | null = null;
  hoverYear: number | null = null;

  private onChange: (value: NgsildYearRange | null) => void = () => {};
  private onTouched: () => void = () => {};

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.cdr.markForCheck();
      }
    }
  }

  get displayValue(): string {
    if (this.yearRange.startYear != null && this.yearRange.endYear != null) {
      return `${this.yearRange.startYear} - ${this.yearRange.endYear}`;
    }
    if (this.yearRange.startYear != null) {
      return `${this.yearRange.startYear} - ...`;
    }
    return '';
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.onTouched();
      this.selectedStartYear = this.yearRange.startYear;
      this.selectedEndYear = this.yearRange.endYear;
    }
    this.cdr.markForCheck();
  }

  getYearsInDecade(): number[] {
    const years: number[] = [];
    for (let i = 0; i < 20; i++) {
      const year = this.currentDecade + i;
      if (year >= this.minYear && year <= this.maxYear) {
        years.push(year);
      }
    }
    return years;
  }

  getDecadeRange(): string {
    const end = Math.min(this.currentDecade + 19, this.maxYear);
    return `${this.currentDecade} - ${end}`;
  }

  yearCellClass(year: number): Record<string, boolean> {
    const disabled = year < this.minYear || year > this.maxYear;
    const isStart = this.isStartYear(year);
    const isEnd = this.isEndYear(year);
    const inRange = this.isInRangeButNotStartOrEnd(year);
    const isCurrent = year === new Date().getFullYear();
    return {
      'ngsild-year-range__cell': true,
      'ngsild-year-range__cell--disabled': disabled,
      'ngsild-year-range__cell--start': isStart,
      'ngsild-year-range__cell--end': isEnd,
      'ngsild-year-range__cell--in-range': inRange,
      'ngsild-year-range__cell--current': isCurrent && !isStart && !isEnd && !inRange,
    };
  }

  isStartYear(year: number): boolean {
    return this.selectedStartYear != null && year === this.selectedStartYear;
  }

  isEndYear(year: number): boolean {
    return this.selectedEndYear != null && year === this.selectedEndYear;
  }

  isInRangeButNotStartOrEnd(year: number): boolean {
    if (
      this.selectedStartYear != null &&
      this.hoverYear != null &&
      this.selectedEndYear == null
    ) {
      const start = this.selectedStartYear;
      const end = this.hoverYear;
      return year > Math.min(start, end) && year < Math.max(start, end);
    }
    if (this.selectedStartYear != null && this.selectedEndYear != null) {
      return year > this.selectedStartYear && year < this.selectedEndYear;
    }
    return false;
  }

  selectYear(year: number): void {
    if (year < this.minYear || year > this.maxYear) return;

    if (
      this.selectedStartYear == null ||
      (this.selectedStartYear != null && this.selectedEndYear != null)
    ) {
      this.selectedStartYear = year;
      this.selectedEndYear = null;
      this.hoverYear = null;
      this.yearRange = { startYear: year, endYear: null };
      this.onChange(this.yearRange);
    } else if (this.selectedStartYear != null && this.selectedEndYear == null) {
      if (year < this.selectedStartYear) {
        this.selectedEndYear = this.selectedStartYear;
        this.selectedStartYear = year;
      } else {
        this.selectedEndYear = year;
      }
      this.yearRange = {
        startYear: this.selectedStartYear,
        endYear: this.selectedEndYear,
      };
      this.onChange(this.yearRange);
      this.isOpen = false;
    }
    this.cdr.markForCheck();
  }

  onYearHover(year: number): void {
    if (this.selectedStartYear != null && this.selectedEndYear == null) {
      this.hoverYear = year;
      this.cdr.markForCheck();
    }
  }

  onYearLeave(): void {
    this.hoverYear = null;
    this.cdr.markForCheck();
  }

  previousDecade(): void {
    const newDecade = this.currentDecade - 20;
    this.currentDecade =
      newDecade >= this.minYear ? newDecade : Math.floor(this.minYear / 20) * 20;
    this.cdr.markForCheck();
  }

  nextDecade(): void {
    const newDecade = this.currentDecade + 20;
    if (newDecade <= this.maxYear) {
      this.currentDecade = newDecade;
      this.cdr.markForCheck();
    }
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.yearRange = { startYear: null, endYear: null };
    this.selectedStartYear = null;
    this.selectedEndYear = null;
    this.hoverYear = null;
    this.onChange(null);
    this.onTouched();
    this.cdr.markForCheck();
  }

  writeValue(value: NgsildYearRange | null): void {
    if (value?.startYear != null && value.endYear != null) {
      this.yearRange = { startYear: value.startYear, endYear: value.endYear };
      this.selectedStartYear = value.startYear;
      this.selectedEndYear = value.endYear;
      this.currentDecade = Math.floor(value.startYear / 20) * 20;
    } else if (value?.startYear != null) {
      this.yearRange = { startYear: value.startYear, endYear: null };
      this.selectedStartYear = value.startYear;
      this.selectedEndYear = null;
      this.currentDecade = Math.floor(value.startYear / 20) * 20;
    } else {
      this.yearRange = { startYear: null, endYear: null };
      this.selectedStartYear = null;
      this.selectedEndYear = null;
      this.currentDecade = Math.max(
        2000,
        Math.floor((new Date().getFullYear() - 20) / 20) * 20,
      );
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: NgsildYearRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
